import React, { useState } from "react";
import Draggable from "react-draggable";
import { Box, Button, Typography, IconButton, Divider } from "@mui/material";
import { useTheme } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import figAgentLogo from "../../assets/fig_agent.png"; // FIG Agent logo
import ProteamLogo from "../../assets/proteam.png";
import JTM from "../../assets/jtm.png";
import PilgrimsLogo from "../../assets/pilgrims.png";
import TekVisionsLogo from "../../assets/tekvisions.png";
import DickandJanesLogo from "../../assets/dickandjanes.png";

// Useful Tools
const usefulTools = [
    {
        name: "Google Sheets",
        url: "https://sheets.google.com",
        logo: "https://w7.pngwing.com/pngs/249/999/png-transparent-google-sheets-logo-and-symbol.png",
    },
    {
        name: "Skype",
        url: "skype:",
        logo: "https://th.bing.com/th/id/OIP.DZtr8Ssjo5BUHKRNRYiw3gHaHa?rs=1&pid=ImgDetMain",
    },
];

// FIG Tools & Services
const figToolsAndServices = [
    {
        name: "USDA Food Data Central",
        url: "https://ai.swancreate.com/usda-fdc",
        logo: figAgentLogo,
    },
    {
        name: "GDSN Product Search",
        url: "https://ai.swancreate.com/gdsn-products",
        logo: figAgentLogo,
    },
    {
        name: "FIG Agent Sandbox",
        url: "https://ai.swancreate.com/sandbox",
        logo: figAgentLogo,
    },
];

// Customers Section
const customers = [
    {
        name: "ProTeam Food Service Advisors",
        url: "https://sailwingman.swancreate.com/ai-pagewrapper?url=https%3A%2F%2Fproteamadvisors.com%2F&apiid=D917B366-E344-4430-B488-98E3BD4C93DC",
        logo: ProteamLogo,
    },
    {
        name: "JTM Food Group",
        url: "https://www.jtmfoodgroup.com/",
        logo: JTM,
    },
    {
        name: "Pilgrims",
        url: "https://pilgrimsfoodservice.com/",
        logo: PilgrimsLogo,
    },
    {
        name: "Dick and Janes Educational Snacks",
        url: "https://sailwingman.swancreate.com/ai-pagewrapper?url=https%3A%2F%2Fwww.educationalsnacks.com&apiid=F8B3BC2D-903D-4CA4-A48B-87DB11A1C943",
        logo: DickandJanesLogo,
    },
    {
        name: "TekVisions",
        url: "https://sailwingman.swancreate.com/ai-pagewrapper?url=https%3A%2F%2Fwww.tekvisions.com%2Fproducts%2F&apiid=1BA362BD-91F8-4100-9CC8-349297B27B90",
        logo: TekVisionsLogo,
    },
];

// Function to render the logo for each service
const getIconForSite = (logo) => (
    <img src={logo} alt="Logo" style={{ width: "24px", height: "24px", borderRadius: "4px", marginRight: "10px" }} />
);

const ApiConnectionsSidebar = () => {
    const theme = useTheme();
    const [windows, setWindows] = useState([]);

    // Function to handle opening links in draggable windows
    const openWindow = (url, name) => {
        setWindows((prev) => [
            ...prev,
            { id: Date.now(), url, name, isFullscreen: false, size: { width: 600, height: 400 } },
        ]);
    };

    // Function to close a window
    const closeWindow = (id) => setWindows((prev) => prev.filter((window) => window.id !== id));

    // Toggle fullscreen state for a window
    const toggleFullscreen = (id) => {
        setWindows((prev) =>
            prev.map((window) =>
                window.id === id
                    ? {
                        ...window,
                        isFullscreen: !window.isFullscreen,
                        size: window.isFullscreen
                            ? { width: 600, height: 400 } // Restore original size
                            : { width: "100vw", height: "100vh" }, // Maximize
                    }
                    : window
            )
        );
    };

    return (
        <Box
            sx={{
                width: "250px",
                padding: theme.spacing(2),
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                borderRight: `1px solid ${theme.palette.divider}`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                boxShadow: theme.shadows[4],
            }}
        >
            {/* Useful Tools Section */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Useful Tools
            </Typography>
            {usefulTools.map((site, index) => (
                <Button
                    key={index}
                    onClick={() => openWindow(site.url, site.name)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                        textTransform: "none",
                        backgroundColor: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        border: `1px solid ${theme.palette.divider}`,
                        justifyContent: "flex-start",
                        width: "100%",
                        height: "40px",
                        "&:hover": { backgroundColor: theme.palette.action.hover },
                    }}
                    startIcon={getIconForSite(site.logo)}
                >
                    {site.name}
                </Button>
            ))}

            <Divider sx={{ width: "100%", my: 2 }} />

            {/* FIG Tools & Services Section */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                FIG Tools & Services
            </Typography>
            {figToolsAndServices.map((site, index) => (
                <Button
                    key={index}
                    onClick={() => openWindow(site.url, site.name)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                        textTransform: "none",
                        backgroundColor: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        border: `1px solid ${theme.palette.divider}`,
                        justifyContent: "flex-start",
                        width: "100%",
                        height: "40px",
                        "&:hover": { backgroundColor: theme.palette.action.hover },
                    }}
                    startIcon={getIconForSite(site.logo)}
                >
                    {site.name}
                </Button>
            ))}

            <Divider sx={{ width: "100%", my: 2 }} />

            {/* Customers Section */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Customers
            </Typography>
            {customers.map((site, index) => (
                <Button
                    key={index}
                    onClick={() => openWindow(site.url, site.name)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                        textTransform: "none",
                        backgroundColor: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        border: `1px solid ${theme.palette.divider}`,
                        justifyContent: "flex-start",
                        width: "100%",
                        height: "40px",
                        "&:hover": { backgroundColor: theme.palette.action.hover },
                    }}
                    startIcon={getIconForSite(site.logo)}
                >
                    {site.name}
                </Button>
            ))}

            {/* Draggable browser-like windows */}
            {windows.map((window) => (
                <Draggable key={window.id}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            backgroundColor: "#fff",
                            border: "2px solid #ccc",
                            zIndex: 1000,
                            width: window.size.width,
                            height: window.size.height,
                        }}
                    >
                        {/* Window header with controls */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "5px",
                                backgroundColor: "navy",
                                color: "orange",
                                fontWeight: "bold",
                            }}
                        >
                            <Typography variant="h6" sx={{ marginLeft: "10px" }}>
                                {window.name}
                            </Typography>
                            <Box>
                                <IconButton
                                    onClick={() => toggleFullscreen(window.id)}
                                    aria-label="fullscreen"
                                    sx={{ color: "orange" }}
                                >
                                    {window.isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                </IconButton>
                                <IconButton
                                    onClick={() => closeWindow(window.id)}
                                    aria-label="close window"
                                    sx={{ color: "orange" }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        <iframe
                            src={window.url}
                            title={window.name}
                            style={{ width: "100%", height: "calc(100% - 40px)", border: "none" }}
                        />
                    </Box>
                </Draggable>
            ))}
        </Box>
    );
};

export default ApiConnectionsSidebar;
